import { Address } from './address';
import { Contact } from './contact';
import { Payment } from './payment';
import type { SyndicInfo, SyndicContactPerson } from './property-group';
import type { UtilitySetup } from './utility-setup';
import {
    RoomDetail,
    PoolDetail,
    GardenDetail,
    GarageDetail,
    SecurityDetail,
    LaundryDetail,
    DressingDetail,
    OfficeDetail,
    StorageDetail,
    BasementDetail,
    AtticDetail,
    BalconyTerraceDetail,
    ParkingDetail,
    BuildingSystems,
    WineCellarDetail,
    HomeTheaterDetail,
    GymDetail,
    SaunaDetail,
    ITInfrastructureDetail,
    HVACDetail,
    CommercialParkingDetail,
    AccessibilityDetail,
    LoadingDockDetail,
    RackingSystemDetail,
    FloorSpecificationDetail,
    FireSuppressionDetail,
    MaterialHandlingDetail,
    StorefrontDetail,
    FootTrafficDetail,
    RetailTechnologyDetail,
    BuildingCharacteristics,
    BBQDetail,
    PlaygroundDetail,
    TennisCourtDetail,
    SolarPanelsDetail,
    EvChargingDetail,
    SmartHomeDetail
} from './property-room-details';

export enum PropertyType {
    Apartment = "apartment",
    House = "house",
    Villa = "villa",
    Office = "office",
    Retail = "retail",
    RetailWarehouse = "retailWarehouse",
    Warehouse = "warehouse",
    Land = "land",
    Other = "other"
}

// Belgian energy performance certificate rating
export enum PEBRating {
    A = "A",
    APlusPlus = "A++",
    APlus = "A+",
    B = "B",
    C = "C",
    D = "D",
    E = "E",
    F = "F",
    G = "G"
}

// Compliance status for technical inspections
export enum ComplianceStatus {
    Passed = "passed",
    MinorIssues = "minorIssues",
    MajorIssues = "majorIssues",
    Failed = "failed",
    Pending = "pending",
    NotApplicable = "notApplicable"
}

// Document types for property
export enum PropertyDocumentType {
    PEBCertificate = "pebCertificate",
    ElectricalInspection = "electricalInspection",
    FloorPlan = "floorPlan",
    CadastralPlan = "cadastralPlan",
    SyndicReport = "syndicReport",
    WaterInspection = "waterInspection",
    GasInspection = "gasInspection",
    SoilCertificate = "soilCertificate",
    AsBuiltPlan = "asBuiltPlan",
    UrbanPlanningCertificate = "urbanPlanningCertificate",
    InsurancePolicy = "insurancePolicy",
    InvoiceBill = "invoiceBill",
    Other = "other"
}

/**
 * Certificate status lifecycle
 */
export enum CertificateStatus {
    Active = 'active',           // Currently valid and in effect
    Expired = 'expired',         // Past expiry date
    Superseded = 'superseded',   // Replaced by newer certificate
    Revoked = 'revoked',         // Officially revoked/cancelled
    Pending = 'pending'          // Awaiting validation/activation
}

/**
 * Building permit types
 */
export enum BuildingPermitType {
    NewConstruction = 'new_construction',
    MajorRenovation = 'major_renovation',
    MinorRenovation = 'minor_renovation',
    Extension = 'extension',
    Demolition = 'demolition',
    ChangeOfUse = 'change_of_use',
    Other = 'other'
}

/**
 * Technical inspection certificate types
 */
export enum TechnicalInspectionType {
    Electrical = 'electrical',
    Gas = 'gas',
    Water = 'water',
    Soil = 'soil',
    Asbestos = 'asbestos'
}

// ========================================
// HISTORICAL CERTIFICATES & PERMITS SYSTEM
// ========================================

/**
 * Base interface for all certificates and permits
 * Provides common fields for historical tracking
 */
export interface BaseCertificate {
    id: string;                        // Unique identifier (UUID)
    certificateNumber: string;         // Official certificate/permit number
    issueDate: Date;                   // When it was issued
    validFrom: Date;                   // When it becomes valid
    expiryDate: Date | null;           // When it expires (null = no expiry)
    validityPeriodYears?: number;      // Validity period in years (for reference)
    
    // Document linkage
    documentUID: string | null;        // Link to Document in DocumentService
    documentURL: string | null;        // Direct URL (fallback/cache)
    
    // Status tracking
    status: CertificateStatus;         // active, expired, superseded, revoked
    supersededBy: string | null;       // ID of certificate that replaced this one
    supersedes: string | null;         // ID of certificate this one replaced
    
    // Issuing authority
    issuedBy: {
        organization: string;
        inspector?: string;
        contactInfo?: string;
    } | null;
    
    // Metadata
    notes: string | null;              // Additional notes
    createdAt: Date;                   // When added to system
    createdBy: string;                 // User ID who added it
    updatedAt: Date;                   // Last update
    updatedBy: string;                 // User ID who last updated it
    
    // Country-specific data (flexible for international use). Callers pull
    // specific keys per country, so the value type stays `unknown` — a
    // `Record<string, any>` here just erases type-checking at every read.
    countryCode?: string;              // ISO country code (e.g., 'BE', 'FR')
    countrySpecificData?: Record<string, unknown>;
}

/**
 * Inspection issue found during technical inspection
 */
export interface InspectionIssue {
    severity: 'critical' | 'major' | 'minor' | 'info';
    category: string;
    description: string;
    location?: string;
    remediation?: string;
    estimatedCost?: number;
}

/**
 * PEB Certificate (Energy Performance)
 */
export interface PEBCertificate extends BaseCertificate {
    type: 'peb';
    rating: PEBRating;                      // A++, A+, A, B, C, D, E, F, G
    primaryEnergyConsumption: number;       // kWh/m²/year
    co2Emissions: number;                   // kg CO2/m²/year
    
    // Detailed metrics (optional)
    metrics?: {
        heatingConsumption?: number;
        coolingConsumption?: number;
        hotWaterConsumption?: number;
        ventilationConsumption?: number;
        lightingConsumption?: number;
        renewableEnergyProduction?: number;
    };
    
    // Recommendations from certificate
    recommendations?: {
        priority: 'high' | 'medium' | 'low';
        description: string;
        estimatedCost?: number;
        estimatedSavings?: number;
    }[];
}

/**
 * Building Permit
 */
export interface BuildingPermit extends BaseCertificate {
    type: 'building_permit';
    permitType: BuildingPermitType;
    
    // Scope of work
    scope: {
        description: string;
        workType: string[];                // e.g., ['renovation', 'extension']
        affectedArea?: number;             // m²
        estimatedValue?: number;           // Estimated work value
    };
    
    // Approval details
    approval: {
        approvedDate: Date;
        approvalAuthority: string;
        conditions?: string[];             // Conditions attached to permit
    };
    
    // Work completion
    workCompleted: boolean;
    completionDate: Date | null;
    finalInspectionDate: Date | null;
    finalInspectionPassed: boolean | null;
}

/**
 * Urbanistic Certificate
 */
export interface UrbanisticCertificate extends BaseCertificate {
    type: 'urbanistic_certificate';
    certificateType: 'positive' | 'negative';
    
    // Zoning information
    zoning: {
        designation: string;
        restrictions: string[];
        allowedUses: string[];
    };
    
    // Urban planning compliance
    compliance: {
        conformsToUrbanPlan: boolean;
        exceptions?: string[];
        pendingInfractions: boolean;
        infractionDetails?: string;
    };
}

/**
 * Technical Inspection Certificate (Electrical, Gas, Water, Soil, Asbestos)
 */
export interface TechnicalInspectionCertificate extends BaseCertificate {
    type: TechnicalInspectionType;
    complianceStatus: ComplianceStatus;    // passed, minorIssues, majorIssues, failed
    
    // Inspection details
    inspection: {
        inspectorName: string;
        inspectorLicense: string | null;
        inspectionDate: Date;
        nextInspectionDue: Date | null;
    };
    
    // Findings
    findings: {
        overallStatus: ComplianceStatus;
        issuesFound: InspectionIssue[];
        recommendations: string[];
        complianceNotes: string | null;
    };
    
    // Type-specific data (flexible for different inspection types and countries)
    typeSpecificData?: {
        // For electrical
        installationAge?: number;
        earthingStatus?: 'compliant' | 'non_compliant';
        circuitBreakerStatus?: 'compliant' | 'non_compliant';
        
        // For gas
        leakTestResult?: 'passed' | 'failed';
        ventilationAdequate?: boolean;
        
        // For soil
        contaminationLevel?: 'none' | 'low' | 'medium' | 'high';
        remediationRequired?: boolean;
        
        // For asbestos
        asbestosPresent?: boolean;
        asbestosLocations?: string[];
        removalPlan?: string;
        
        // Flexible for country-specific requirements. Values are read per
        // country with narrowed types at the call site, so we use `unknown`
        // here — callers must narrow before consuming.
        [key: string]: unknown;
    };
}

/**
 * Échéances annuelles saisies à la main sur le bien. Purement additif : un bien
 * sans ces dates ne lève aucune carte. Forme lue telle quelle par les détecteurs
 * `detectPropertyTaxDue` / `detectMeterReadingDue` — la changer les casse.
 */
export interface PropertyCompliance {
    /** Précompte immobilier / taxe foncière. Seuls le mois et le jour comptent. */
    propertyTaxDueOn?: Date | null;
    /** Relevé annuel des compteurs, hors déménagement. */
    meterReadingDueOn?: Date | null;
}

export class Property {
    // Mandatory fields
    propertyUID!: string;
    workspaceId!: string;  // Workspace-based architecture (replaces ownerUID)
    name!: string;
    type!: PropertyType;
    address!: Address;

    // Unit-related fields
    isUnit = false;
    buildingInfo: BuildingReference | null = null;
    
    // Construction type for tax purposes
    isNewConstruction = false;

    // Optional fields
    description: string | null = null;
    yearBuilt: number | null = null;
    propertyCondition: string | null = null; // new, as_new, excellent, very_good, good, fair, needs_work, renovation_required
    isRenovated: boolean = false;
    renovationYear: number | null = null;
    buildingUID: string | null = null;  // Reference to a building/complex if applicable
    buildingGroupUID: string | null = null;  // Reference to PropertyGroup (building) if this property is a unit
    // Quotités/tantièmes — lot's ownership share, positive integer (BE convention: /1000).
    // Optional in general; REQUIRED to run an owner-side building charge distribution (décompte).
    quotites: number | null = null;
    // Compteurs et contrats d'un bien seul (#680). Pour un lot d'immeuble, c'est
    // le tableau de l'immeuble qui fait foi. Lire via `normalizeUtilities`.
    utilities?: UtilitySetup[] | null;

    characteristics: PropertyCharacteristics = {
        bedrooms: null,
        bathrooms: null,
        totalArea: null,
        parkingSpaces: null
    };

    financials: PropertyFinancials = {
        purchaseInfo: {
            date: null,
            price: null,
            brokerCommission: null,
            legalFees: null,
            otherFees: null
        },
        currentValue: null,
        payments: [],
        parameters: {}
    };

    rental: PropertyRental = {
        isRented: false,
        currentContractUID: null,
        currentTenantUID: null,
        initialRent: null,
        currentRent: null,
        nextIndexationDate: null,
        rentalHistory: []
    };

    surfaces: PropertySurface = {
        land: null,
        buildingSurface: null
    };

    // Legal information with historical permits/certificates
    legal: PropertyLegal = {
        zoning: null,
        landRegistryNumber: null,
        cadastralReference: null,
        cadastralIncome: null,
        propertyTax: null,
        buildingPermits: [],
        urbanisticCertificates: [],
        easements: {
            hasEasements: false,
            details: null,
            hasPreemptiveRights: false,
            preemptiveRightsDetails: null
        },
        // Compatibility getters (computed properties)
        get buildingPermit() {
            return this.buildingPermits.length > 0 
                ? {
                    number: this.buildingPermits[0].certificateNumber,
                    date: this.buildingPermits[0].issueDate
                  }
                : null;
        },
        get urbanisticCertificate() {
            return this.urbanisticCertificates.length > 0
                ? {
                    number: this.urbanisticCertificates[0].certificateNumber,
                    date: this.urbanisticCertificates[0].issueDate
                  }
                : null;
        },
        get hasUrbanisticInfraction() {
            return this.urbanisticCertificates.some(cert => 
                cert.compliance?.pendingInfractions === true
            );
        }
    };

    /**
     * Échéances annuelles que le propriétaire date à la main — rien ici ne peut
     * être déduit : le calendrier du précompte est régional, et le relevé annuel
     * dépend du fournisseur.
     *
     * Lu côté serveur par `detectPropertyTaxDue` et `detectMeterReadingDue`
     * (`backend/functions/src/cockpit/dailyCockpitSweep.ts`) : seuls le mois et
     * le jour comptent, l'année est recalculée à chaque passage. Sans date, aucune
     * carte n'est levée — c'est voulu.
     */
    compliance?: PropertyCompliance;

    // Technical information with historical certificates/inspections
    technical: PropertyTechnical = {
        pebCertificates: [],
        electricalInspections: [],
        gasInspections: [],
        waterInspections: [],
        soilCertificates: [],
        asbestosInspections: [],
        floodRisk: {
            zoneCategory: null,
            lastAssessmentDate: null
        },
        // Compatibility getters (computed properties)
        get peb() {
            const cert = this.pebCertificates.find(c => c.status === 'active') || this.pebCertificates[0];
            return cert ? {
                certificateNumber: cert.certificateNumber,
                rating: cert.rating,
                issueDate: cert.issueDate,
                expiryDate: cert.expiryDate,
                primaryEnergyConsumption: cert.primaryEnergyConsumption,
                co2Emissions: cert.co2Emissions,
                documentURL: cert.documentURL
            } : null;
        },
        get electrical() {
            const insp = this.electricalInspections.find(c => c.status === 'active') || this.electricalInspections[0];
            return insp ? {
                status: insp.complianceStatus,
                certificateNumber: insp.certificateNumber,
                inspectionDate: insp.inspection.inspectionDate,
                expiryDate: insp.expiryDate,
                documentURL: insp.documentURL,
                notes: insp.notes
            } : null;
        },
        get gas() {
            const insp = this.gasInspections.find(c => c.status === 'active') || this.gasInspections[0];
            return insp ? {
                status: insp.complianceStatus,
                certificateNumber: insp.certificateNumber,
                inspectionDate: insp.inspection.inspectionDate,
                expiryDate: insp.expiryDate,
                documentURL: insp.documentURL
            } : null;
        },
        get water() {
            const insp = this.waterInspections.find(c => c.status === 'active') || this.waterInspections[0];
            return insp ? {
                status: insp.complianceStatus,
                certificateNumber: insp.certificateNumber,
                inspectionDate: insp.inspection.inspectionDate,
                documentURL: insp.documentURL
            } : null;
        },
        get soil() {
            const cert = this.soilCertificates.find(c => c.status === 'active') || this.soilCertificates[0];
            return cert ? {
                certificateNumber: cert.certificateNumber,
                issueDate: cert.issueDate,
                category: cert.typeSpecificData?.contaminationLevel || null,
                documentURL: cert.documentURL
            } : null;
        },
        get asbestos() {
            const insp = this.asbestosInspections.find(c => c.status === 'active') || this.asbestosInspections[0];
            return insp ? {
                status: insp.complianceStatus,
                inspectionDate: insp.inspection.inspectionDate,
                documentURL: insp.documentURL
            } : null;
        }
    };

    // Building & strata management
    building: PropertyBuilding = {
        syndicInfo: {
            name: null,
            contact: null,
            fees: null,
            paymentFrequency: null
        },
        syndicReports: [],
        commonAreas: [],
        buildingRules: null
    };

    // Technical plans
    plans: PropertyPlan[] = [];
    
    // Property documents with type categorization
    documents: PropertyDocument[] = [];

    // Property images are now stored as documents with type 'Photo'
    // Use DocumentService to retrieve photos with entityType='property' and type='Photo'

    keyContacts: PropertyKeyContacts = {
        syndic: [],
        concierge: [],
        others: []
    };

    createdAt: Date = new Date();
    updatedAt: Date = new Date();
    isPubliclyListed = false;
    listingType: 'sale' | 'lease' | null = null;
    deleted = false;
    deletedAt: Date | null = null;

    // Billing grace period (7-day window to add payment method for billable properties)
    billingGracePeriodEnds: Date | null = null;  // When the grace period expires
    requiresPaymentSetup: boolean = false;       // Flag indicating payment method required
    gracePeriodWarningShown: boolean = false;    // Track if user has been warned

    // Marqueur "créé pendant le bypass de consentement facturation" (mode
    // temporaire "création illimitée") — jamais de période de grâce posée sur
    // ce bien, donc pas de suppression/relance automatique. Sert uniquement à
    // le retrouver pour un rattrapage manuel de facturation plus tard.
    billingConsentBypassed: boolean = false;
    billingConsentBypassedAt: Date | null = null;

    // Workspace-based architecture: Audit trail
    createdBy: string = '';      // User ID who created the property
    lastModifiedBy: string = ''; // User ID who last modified the property

    constructor(propertyData: Partial<Property> = {}) {
        Object.assign(this, propertyData);
        
        // Handle Firestore Timestamp objects
        if (this.financials?.purchaseInfo?.date && 
            typeof this.financials.purchaseInfo.date === 'object' && 
            'toDate' in this.financials.purchaseInfo.date && 
            typeof this.financials.purchaseInfo.date.toDate === 'function') {
            this.financials.purchaseInfo.date = this.financials.purchaseInfo.date.toDate();
        }
        
        // Handle created and updated dates
        if (this.createdAt && typeof this.createdAt === 'object' && 'toDate' in this.createdAt && typeof this.createdAt.toDate === 'function') {
            this.createdAt = this.createdAt.toDate();
        }
        
        if (this.updatedAt && typeof this.updatedAt === 'object' && 'toDate' in this.updatedAt && typeof this.updatedAt.toDate === 'function') {
            this.updatedAt = this.updatedAt.toDate();
        }
        
        // Handle billing grace period end date
        if (this.billingGracePeriodEnds && typeof this.billingGracePeriodEnds === 'object' && 'toDate' in this.billingGracePeriodEnds && typeof this.billingGracePeriodEnds.toDate === 'function') {
            this.billingGracePeriodEnds = this.billingGracePeriodEnds.toDate();
        }

        // Handle billing-consent-bypass timestamp
        if (this.billingConsentBypassedAt && typeof this.billingConsentBypassedAt === 'object' && 'toDate' in this.billingConsentBypassedAt && typeof this.billingConsentBypassedAt.toDate === 'function') {
            this.billingConsentBypassedAt = this.billingConsentBypassedAt.toDate();
        }
        
        // Handle rental history dates
        if (this.rental?.nextIndexationDate && 
            typeof this.rental.nextIndexationDate === 'object' && 
            'toDate' in this.rental.nextIndexationDate && 
            typeof this.rental.nextIndexationDate.toDate === 'function') {
            this.rental.nextIndexationDate = this.rental.nextIndexationDate.toDate();
        }
        
        // Process rental history if it exists
        if (this.rental?.rentalHistory && Array.isArray(this.rental.rentalHistory)) {
            this.rental.rentalHistory = this.rental.rentalHistory.map(entry => {
                // Process startDate
                if (entry.startDate && 
                    typeof entry.startDate === 'object' && 
                    'toDate' in entry.startDate && 
                    typeof entry.startDate.toDate === 'function') {
                    entry.startDate = entry.startDate.toDate();
                }
                
                // Process endDate if it exists
                if (entry.endDate && 
                    typeof entry.endDate === 'object' && 
                    'toDate' in entry.endDate && 
                    typeof entry.endDate.toDate === 'function') {
                    entry.endDate = entry.endDate.toDate();
                }
                
                return entry;
            });
        }
        
        // Process PEB dates
        if (this.technical?.peb?.issueDate && 
            typeof this.technical.peb.issueDate === 'object' && 
            'toDate' in this.technical.peb.issueDate && 
            typeof this.technical.peb.issueDate.toDate === 'function') {
            this.technical.peb.issueDate = this.technical.peb.issueDate.toDate();
        }
        
        if (this.technical?.peb?.expiryDate && 
            typeof this.technical.peb.expiryDate === 'object' && 
            'toDate' in this.technical.peb.expiryDate && 
            typeof this.technical.peb.expiryDate.toDate === 'function') {
            this.technical.peb.expiryDate = this.technical.peb.expiryDate.toDate();
        }
        
        // Process electrical inspection dates
        if (this.technical?.electrical?.inspectionDate && 
            typeof this.technical.electrical.inspectionDate === 'object' && 
            'toDate' in this.technical.electrical.inspectionDate && 
            typeof this.technical.electrical.inspectionDate.toDate === 'function') {
            this.technical.electrical.inspectionDate = this.technical.electrical.inspectionDate.toDate();
        }
        
        if (this.technical?.electrical?.expiryDate && 
            typeof this.technical.electrical.expiryDate === 'object' && 
            'toDate' in this.technical.electrical.expiryDate && 
            typeof this.technical.electrical.expiryDate.toDate === 'function') {
            this.technical.electrical.expiryDate = this.technical.electrical.expiryDate.toDate();
        }

        // Note: accessControl and ownerUID are deprecated in workspace v2.0 architecture
        // All access control is now managed through workspace membership
        // Keeping commented for backward compatibility during data migration
        
        /* OLD v1.0 Property Sharing System - DEPRECATED
        if (!this.accessControl && this.ownerUID) {
            this.accessControl = {
                ownerId: this.ownerUID,
                isShared: false,
                sharedUserCount: 0,
                sharedWith: {}
            };
        }
        
        if (!this.accessControlVersion) {
            this.accessControlVersion = 1;
        }
        
        if (this.lastAccessControlUpdate && 
            typeof this.lastAccessControlUpdate === 'object' && 
            'toDate' in this.lastAccessControlUpdate && 
            typeof this.lastAccessControlUpdate.toDate === 'function') {
            this.lastAccessControlUpdate = this.lastAccessControlUpdate.toDate();
        } else if (!this.lastAccessControlUpdate) {
            this.lastAccessControlUpdate = new Date();
        }
        */
    }

    getTotalRentalIncome(): number {
        if (!this.rental || !this.rental.rentalHistory || !Array.isArray(this.rental.rentalHistory)) {
            return 0;
        }
        
        return this.rental.rentalHistory.reduce((total, entry) => {
            const monthsDiff = this.getMonthsDifference(entry.startDate, entry.endDate || new Date());
            return total + (entry.monthlyRent * monthsDiff);
        }, 0);
    }

    private getMonthsDifference(startDate: Date, endDate: Date): number {
        const months = (endDate.getFullYear() - startDate.getFullYear()) * 12;
        return months + endDate.getMonth() - startDate.getMonth();
    }
}

export interface PropertyCharacteristics {
    bedrooms: number | null;
    bathrooms: number | null;
    kitchens?: number | null;
    livingRooms?: number | null;
    totalArea: number | null;
    parkingSpaces: number | null;
    
    // Additional characteristics
    floors?: number | null;
    balconies?: number | null;
    terraces?: number | null;
    
    // Commercial characteristics
    officeSpaces?: number | null;
    meetingRooms?: number | null;
    
    // Retail-specific characteristics
    salesFloors?: number | null;
    storageAreas?: number | null;
    salesFloorArea?: number | null;
    storageArea?: number | null;
    shopFrontWidth?: number | null;
    ceilingHeight?: number | null;
    floorCount?: number | null;
    changeRooms?: number | null;
    footfallRate?: number | null;
    
    // Retail Warehouse characteristics
    retailArea?: number | null;
    warehouseArea?: number | null;
    officeArea?: number | null;
    loadingDocks?: number | null;
    customerParkingSpaces?: number | null;
    staffParkingSpaces?: number | null;
    driveInDoors?: number | null;
    
    // Warehouse characteristics
    // warehouseArea already defined above
    floorLoadCapacity?: number | null;
    storageRacks?: number | null;
    
    // Office characteristics
    totalOfficeArea?: number | null;
    officeRooms?: number | null;
    workstations?: number | null;
    receptionArea?: number | null;
    
    // Features
    features?: string[];
    
    // ==================== NEW: DETAILED ROOM AND FEATURE INFORMATION ====================
    // Generic room details array - works for ALL property types (apartments, houses, offices, warehouses, etc.)
    roomDetails?: RoomDetail[];
    
    // Shared feature details (applicable across multiple property types)
    garageDetails?: GarageDetail | null;
    poolDetails?: PoolDetail | null;
    gardenDetails?: GardenDetail | null;
    laundryDetails?: LaundryDetail | null;
    securityDetails?: SecurityDetail | null;
    dressingDetails?: DressingDetail[];
    officeDetails?: OfficeDetail | null;
    storageDetails?: StorageDetail[];
    basementDetails?: BasementDetail | null;
    atticDetails?: AtticDetail | null;
    balconyTerraceDetails?: BalconyTerraceDetail[];
    parkingDetails?: ParkingDetail | null;
    
    // Villa/luxury residential features
    wineCellarDetails?: WineCellarDetail | null;
    homeTheaterDetails?: HomeTheaterDetail | null;
    gymDetails?: GymDetail | null;
    saunaDetails?: SaunaDetail | null;
    bbqDetails?: BBQDetail | null;
    playgroundDetails?: PlaygroundDetail | null;
    tennisCourtDetails?: TennisCourtDetail | null;
    solarPanelsDetails?: SolarPanelsDetail | null;
    evChargingDetails?: EvChargingDetail | null;
    smartHomeDetails?: SmartHomeDetail | null;
    
    // Commercial/office features
    itInfrastructureDetails?: ITInfrastructureDetail | null;
    hvacDetails?: HVACDetail | null;
    commercialParkingDetails?: CommercialParkingDetail | null;
    accessibilityDetails?: AccessibilityDetail | null;
    
    // Warehouse/industrial features
    loadingDockDetails?: LoadingDockDetail | null;
    rackingSystemDetails?: RackingSystemDetail | null;
    floorSpecificationDetails?: FloorSpecificationDetail | null;
    fireSuppressionDetails?: FireSuppressionDetail | null;
    materialHandlingDetails?: MaterialHandlingDetail | null;
    
    // Retail features
    storefrontDetails?: StorefrontDetail | null;
    footTrafficDetails?: FootTrafficDetail | null;
    retailTechnologyDetails?: RetailTechnologyDetail | null;
    
    // Building systems and utilities (heating, cooling, internet, etc.)
    buildingSystems?: BuildingSystems | null;

    // Building-level characteristics (structure, infrastructure, amenities, sustainability)
    // Used for standalone properties or inherited from PropertyGroup for units in buildings
    buildingCharacteristics?: BuildingCharacteristics | null;

    // Per-field local overrides applied on top of the building base when this unit
    // is attached to a building (buildingGroupUID set). The building is the read-through
    // source of truth; any top-level field set here overrides the inherited value for
    // this unit only. Null/absent => the unit inherits everything from the building.
    buildingCharacteristicsOverrides?: Partial<BuildingCharacteristics> | null;

    // Unit-level building characteristics backup (when part of a building)
    // These are hidden when the property is part of a building and restored when detached
    unitBuildingCharacteristics?: BuildingSystems | null;

    // ==================== BUILDING FEATURES (FOR BUILDING PROPERTY TYPE) ====================
    // Gym/Fitness Center
    gym?: {
        sqm?: number;
        equipment?: string[];
        openingHours?: string;
    } | null;

    // Swimming Pool
    pool?: {
        location?: 'indoor' | 'outdoor' | 'both';
        type?: 'lap_pool' | 'recreational' | 'infinity' | 'heated';
        length?: number;
        width?: number;
        depth?: number;
    } | null;

    // Building Parking (different from unit parking)
    buildingParking?: {
        totalSpaces?: number;
        parkingType?: 'underground' | 'surface' | 'multilevel' | 'mixed';
        visitorSpaces?: number;
        evChargingStations?: number;
    } | null;

    // Concierge Service
    concierge?: {
        hasService?: boolean;
        hoursOfOperation?: string;
        services?: string[];
    } | null;

    // Common Areas (different from PropertyBuilding.commonAreas)
    commonAreas?: {
        type: string;
        name?: string;
        sqm?: number;
        description?: string;
    }[] | null;

    // ==================== SYNDIC & REPORTS (FOR BUILDING PROPERTY TYPE) ====================
    syndicInfo?: PropertySyndicInfo | null;
    monthlyCharges?: PropertyMonthlyCharges | null;
    buildingDocuments?: BuildingDocument[] | null;
    generalAssemblies?: GeneralAssembly[] | null;
}

/**
 * Syndic types for a building-type property.
 *
 * These are now aliases of the CANONICAL `SyndicInfo` / `SyndicContactPerson` defined in
 * property-group.ts (unified 2026-05-29). Kept as named re-exports so existing imports of
 * `PropertySyndicInfo` / `PropertySyndicContactPerson` continue to work. Stored on
 * `Property.characteristics.syndicInfo`.
 */
export type PropertySyndicContactPerson = SyndicContactPerson;
export type PropertySyndicInfo = SyndicInfo;

/** Recurring building-level charges (HOA / syndic). */
export interface PropertyMonthlyCharges {
    totalAmount?: number;
    currency?: string;
    paymentFrequency?: 'monthly' | 'quarterly' | 'annually';
    lastUpdate?: Date;
    breakdown?: {
        description: string;
        amount: number;
    }[];
}

/** Document attached to a building (insurance, rules, inspection, …). */
export interface BuildingDocument {
    name: string;
    type: string;
    fileUrl?: string;
    uploadDate: Date;
    size?: number;
    uploader?: string;
}

/** Single vote or decision recorded at a general assembly. */
export interface GeneralAssemblyDecision {
    title: string;
    description?: string;
    voteType: 'for/against' | 'unanimous' | 'multiple_choice';
    votesFor?: number;
    votesAgainst?: number;
    votesAbstain?: number;
    outcome: 'approved' | 'rejected' | 'deferred';
    notes?: string;
}

/** Minutes record for a general assembly of co-owners. */
export interface GeneralAssembly {
    title: string;
    date: Date;
    location?: string;
    description?: string;
    attendees?: number;
    totalEligibleVoters?: number;
    quorumReached?: boolean;
    minutesAttached?: boolean;
    minutesUrl?: string;
    votes?: number;
    decisions?: GeneralAssemblyDecision[];
}

/**
 * Financial parameters used for projections, fallback rent estimates, and
 * preserving the per-field acquisition / legal-fees breakdown the form
 * captures at save time so it can be re-displayed in edit mode.
 */
export interface PropertyFinancialParameters {
    /** User-entered expected rent — fallback when no active contract is set. */
    baseExpectedRent?: number;

    /** Per-field breakdown of purchase costs, captured on add/edit. */
    detailedBreakdown?: {
        acquisitionCosts?: {
            brokerCommission?: number;
            agencyFees?: number;
            searchFees?: number;
            otherCosts?: number;
            total?: number;
        };
        legalFeesBreakdown?: {
            vatAmount?: number;
            registrationFees?: number;
            notaryFees?: number;
            legalAdvisorFees?: number;
            otherLegalCosts?: number;
            total?: number;
        };
    };

    // Projection assumptions — per-property overrides of the system defaults.

    // Operating expenses
    /** Annual property tax (€). */
    annualPropertyTax?: number;
    /** Annual insurance premium (€). */
    annualInsurance?: number;
    /** Maintenance reserve as % of property value. */
    maintenancePercentage?: number;
    /** Pre-computed absolute maintenance amount (€/yr), derived from percentage × value. */
    maintenanceAbsoluteAmount?: number;
    /** Management fee as % of rental income. */
    managementFeePercentage?: number;
    /** Pre-computed absolute management fee (€/yr), derived from percentage × rent. */
    managementFeeAbsoluteAmount?: number;
    /** Other monthly expenses — utilities, HOA, etc. */
    otherMonthlyExpenses?: number;

    // Market assumptions (percentages expressed as decimals, e.g. 0.03 = 3%, per callers)
    /** Expected vacancy rate. */
    vacancyRate?: number;
    /** Annual inflation assumption. */
    annualInflation?: number;
    /** Annual rent-increase rate applied to projections. */
    annualRentIncrease?: number;
    /** Annual appreciation assumption for the property value. */
    annualAppreciation?: number;
    /** Whether the settings UI exposes advanced market controls. */
    advancedMarketMode?: boolean;

    // Projection / data-quality toggles
    /** Skip standard-expense defaults and use only concrete numbers below. */
    useRealDataOnly?: boolean;
    /** Apply standard-expense assumptions when concrete numbers are missing. */
    assumeStandardExpenses?: boolean;
    /** Use market rent when no contract data is available. */
    assumeMarketRent?: boolean;
    /** Number of years to run cashflow projections over. */
    projectionYears?: number;
}

interface PropertyFinancials {
    purchaseInfo: {
        date: Date | null;
        price: number | null;
        brokerCommission: number | null;
        legalFees: number | null;
        otherFees: number | null;
    };
    currentValue: number | null;
    payments: Payment[];
    parameters?: PropertyFinancialParameters;
    /**
     * Timestamp of the first time the user explicitly confirmed the financial
     * assumptions (via the Finance-tab setup gate or the Settings form). While
     * this is unset AND `parameters` is empty, the Finance tab shows the
     * confirm-assumptions gate instead of figures derived from silent defaults.
     */
    parametersConfirmedAt?: Date | null;
}

interface PropertyRental {
    isRented: boolean;
    currentContractUID: string | null;
    currentTenantUID: string | null;
    initialRent: number | null;
    currentRent: number | null;
    nextIndexationDate: Date | null;
    rentalHistory: RentalHistoryEntry[];
}

interface RentalHistoryEntry {
    contractUID: string;
    tenantUID: string;
    startDate: Date;
    endDate: Date | null;
    monthlyRent: number;
}

interface PropertySurface {
    land: number | null;
    buildingSurface: number | null;
    netBuildingSurface?: number | null;
    
    // Retail-specific surfaces (these might be different from characteristics)
    salesFloorArea?: number | null;
    storageArea?: number | null;
    displayArea?: number | null;
}

/**
 * Property Legal Information
 * Includes static legal info and historical certificates/permits
 */
interface PropertyLegal {
    // Static legal info
    zoning: string | null;
    landRegistryNumber: string | null;
    cadastralReference: string | null;
    cadastralIncome: number | null;
    propertyTax: number | null;
    
    // Historical certificates/permits (NEW - Arrays for complete history!)
    buildingPermits: BuildingPermit[];
    urbanisticCertificates: UrbanisticCertificate[];
    
    // Easements (keep as single object)
    easements: {
        hasEasements: boolean;
        details: string | null;
        hasPreemptiveRights: boolean;
        preemptiveRightsDetails: string | null;
    };
    
    // Compatibility properties (readonly computed from arrays)
    readonly buildingPermit?: { number: string; date: Date; } | null;
    readonly urbanisticCertificate?: { number: string; date: Date; } | null;
    readonly hasUrbanisticInfraction?: boolean;
}

interface PropertyKeyContacts {
    syndic: Contact[];
    concierge: Contact[];
    others: Contact[];
}

// New interfaces for Belgian technical requirements

/**
 * Property Technical Information
 * Includes historical certificates and inspections
 */
interface PropertyTechnical {
    // Historical certificates (NEW - Arrays for complete history!)
    pebCertificates: PEBCertificate[];
    electricalInspections: TechnicalInspectionCertificate[];
    gasInspections: TechnicalInspectionCertificate[];
    waterInspections: TechnicalInspectionCertificate[];
    soilCertificates: TechnicalInspectionCertificate[];
    asbestosInspections: TechnicalInspectionCertificate[];
    
    // Non-certificate data (keep as single object)
    floodRisk: {
        zoneCategory: string | null;
        lastAssessmentDate: Date | null;
    };
    
    // Compatibility properties (readonly computed from arrays)
    readonly peb?: {
        certificateNumber: string | null;
        rating: PEBRating | null;
        issueDate: Date | null;
        expiryDate: Date | null;
        primaryEnergyConsumption: number | null;
        co2Emissions: number | null;
        documentURL: string | null;
    } | null;
    readonly electrical?: {
        status: ComplianceStatus | null;
        certificateNumber: string | null;
        inspectionDate: Date | null;
        expiryDate: Date | null;
        documentURL: string | null;
        notes: string | null;
    } | null;
    readonly gas?: {
        status: ComplianceStatus | null;
        certificateNumber: string | null;
        inspectionDate: Date | null;
        expiryDate: Date | null;
        documentURL: string | null;
    } | null;
    readonly water?: {
        status: ComplianceStatus | null;
        certificateNumber: string | null;
        inspectionDate: Date | null;
        documentURL: string | null;
    } | null;
    readonly soil?: {
        certificateNumber: string | null;
        issueDate: Date | null;
        category: string | null;
        documentURL: string | null;
    } | null;
    readonly asbestos?: {
        status: ComplianceStatus | null;
        inspectionDate: Date | null;
        documentURL: string | null;
    } | null;
}

interface PropertyBuilding {
    syndicInfo: {
        name: string | null;
        contact: Contact | null;
        fees: number | null;
        paymentFrequency: 'monthly' | 'quarterly' | 'annually' | null;
    };
    syndicReports: SyndicReport[];
    commonAreas: CommonArea[];
    buildingRules: string | null; // URL to building rules document
}

interface SyndicReport {
    title: string;
    date: Date;
    documentURL: string | null;
    highlights: string | null;
}

interface CommonArea {
    name: string;
    description: string | null;
    sharedCost: boolean;
}

interface PropertyPlan {
    name: string;
    type: 'floorPlan' | 'cadastral' | 'technical' | 'other';
    floor?: number;
    documentURL: string;
    uploadDate: Date;
}

interface PropertyDocument {
    documentUID: string;
    name: string;
    type: PropertyDocumentType;
    uploadDate: Date;
    documentURL: string;
    expiryDate?: Date;
    notes?: string;
}

// Interface for building reference when a property is a unit
export interface BuildingReference {
    buildingUID: string;
    buildingName: string;
    unitNumber: string | null;
    floor: number | null;
    inheritedAddress: boolean;
}